import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Trophy, Globe, Waves, Target, Award  } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { statisticsAPI, tourStopsAPI } from '../services/api';
import { partnersAPI } from '../services/api';
import HomeSlider from '../components/HomeSlider';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(true); // State for popup
  const [stats, setStats] = useState({
    federations: 15,
    tour_stops: 50,
    athletes: 300,
    experience: 8
  });
  const [tourStops, setTourStops] = useState<any[]>([]);
  const { t, language } = useLanguage();
  const [mainPartners, setMainPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [supportPartners, setSupportPartners] = useState<any[]>([]);

  useEffect(() => {
    setIsVisible(true);
    setIsPopupOpen(true); // Show popup on page load
    fetchStats();
    fetchTourStops();
    fetchPartners();
  }, [language]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      
      // Fetch main partners (title, official, institutional)
      const mainResponse = await partnersAPI.getAll({ 
        category: 'title,official,institutional' 
      });
      
      // Fetch support partners (support, equipment)
      const supportResponse = await partnersAPI.getAll({ 
        category: 'support,equipment' 
      });
      
      if (mainResponse.records) {
        setMainPartners(mainResponse.records.map((partner: any) => ({
          ...partner,
          description: language === 'fr' ? partner.description_fr : partner.description_en,
          testimonial: language === 'fr' ? partner.testimonial_fr : partner.testimonial_en
        })));
      }

      if (supportResponse.records) {
        setSupportPartners(supportResponse.records);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      // Fallback data
      setMainPartners([
        {
          name: 'Red Bull',
          category: t('partners.category.title'),
          logo_url: 'https://images.pexels.com/photos/1650299/pexels-photo-1650299.jpeg?auto=compress&cs=tinysrgb&w=200',
          description: language === 'fr' 
            ? 'Partenaire principal pour le développement du surf extrême et des événements majeurs.'
            : 'Main partner for extreme surfing development and major events.',
          partnership_since: 2023,
          website: 'redbull.com',
          testimonial: language === 'fr'
            ? 'L\'ASCsurf représente l\'avenir du surf mondial. Nous sommes fiers de soutenir cette vision.'
            : 'ASCsurf represents the future of world surfing. We are proud to support this vision.'
        }
      ]);
      
      setSupportPartners([
        { name: 'Rip Curl', category: t('partners.category.equipment'), logo: 'RC' },
        { name: 'Billabong', category: 'Vêtements', logo: 'BB' },
        { name: 'FCS Fins', category: 'Accessoires', logo: 'FCS' },
        { name: 'Futures Fins', category: 'Accessoires', logo: 'FF' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await statisticsAPI.getAll();
      setStats({
        federations: response.federations || 15,
        tour_stops: response.tour_stops || 50,
        athletes: response.athletes || 300,
        experience: 8
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTourStops = async () => {
    try {
      const response = await tourStopsAPI.getAll(); 
      if (response.records) {
        setTourStops(response.records.slice(0, 4)); 
      }
    } catch (error) {
      console.error('Error fetching tour stops:', error);
      // Fallback to mock data
      const mockTourStops = [
        {
          country: 'Sénégal',
          location: 'Yoff Beach',
          image_url: 'https://images.pexels.com/photos/1430672/pexels-photo-1430672.jpeg?auto=compress&cs=tinysrgb&w=800',
          month: 'Mars'
        },
        {
          country: 'Côte d\'Ivoire',
          location: 'Assinie',
          image_url: 'https://images.pexels.com/photos/1619317/pexels-photo-1619317.jpeg?auto=compress&cs=tinysrgb&w=800',
          month: 'Avril'
        },
        {
          country: 'Liberia',
          location: 'Robertsport',
          image_url: 'https://images.pexels.com/photos/1650299/pexels-photo-1650299.jpeg?auto=compress&cs=tinysrgb&w=800',
          month: 'Mai'
        },
        {
          country: 'Maroc',
          location: 'Taghazout',
          image_url: 'https://images.pexels.com/photos/1761279/pexels-photo-1761279.jpeg?auto=compress&cs=tinysrgb&w=800',
          month: 'Juin'
        }
      ];
      setTourStops(mockTourStops);
    }
  };

  const statsData = [
    { number: stats.federations.toString(), label: t('home.stats.countries'), icon: Globe },
    { number: `${stats.tour_stops}+`, label: t('home.stats.competitions'), icon: Trophy },
    { number: `6000+`, label: t('home.stats.athletes'), icon: Users },
    { number: stats.experience.toString(), label: t('home.stats.experience'), icon: Waves }
  ];

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="overflow-x-hidden">
{isPopupOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleClosePopup} // Add click handler to the overlay
        >
          <div 
            className="relative rounded-lg p-6 w-full max-w-4xl mx-4"
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside the popup from closing it
          >
            <button
              onClick={handleClosePopup}
              className="absolute top-3 right-3 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg text-gray-800 hover:text-gray-900"
              aria-label="Close popup"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="w-full max-w-4xl max-h-[80vh]">
              <img
                className="w-full h-auto max-h-[80vh] object-contain"
                src="/afficheliberia.jpg"
                alt="Affiche Liberia"
              />
            </div>
          </div>
        </div>
      )}

      {/* Hero Slider */}
      <HomeSlider />

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Africa Surf Tour Preview */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              
Championnat d'Afrique des Nations {/*t('home.tour.title')*/}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.tour.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tourStops.map((stop, index) => (
              <div key={index} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={stop.image_url} 
                    alt={`${stop.location}, ${stop.country}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {stop.month} {stop.year}
                  </div>
                  
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{stop.country}</h3>
                  <p className="text-gray-600">{stop.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/africa-surf-tour" 
              className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
            >
              <span>{t('home.tour.cta')}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-teal-600"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {t('home.mission.title')}
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {t('home.mission.description')}
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                  <span className="text-gray-300">{t('home.mission.point1')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                  <span className="text-gray-300">{t('home.mission.point2')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                  <span className="text-gray-300">{t('home.mission.point3')}</span>
                </div>
              </div>
              <div className="mt-8">
                <Link 
                  to="/about" 
                  className="inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors duration-300"
                >
                  <span>{t('home.mission.cta')}</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://i.imgur.com/4aPCiTo.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Surfeur africain au coucher du soleil"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-ocean-900 mb-4">
              {t('partners.main.title')}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {t('partners.main.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {supportPartners.map((partner, index) => (
              <div key={index} className="group text-center">
                <div className="relative overflow-hidden rounded-xl bg-white p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    className="w-full h-20 object-cover rounded-lg mb-3 group-hover:scale-105 transition-transform duration-300"
                  />
                  <h3 className="text-sm font-semibold text-ocean-900 mb-1 line-clamp-2">{partner.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
            >
              {t('home.cta.contact')}
            </Link>
            <Link 
              to="/news" 
              className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
            >
              {t('home.cta.news')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;